import type { PageAuthors } from 'types/Author';
import type { Route, RoutesMap } from 'types/PageData';
import type { Author } from 'types/contentful/generated/graphql';
import routesCache from '.cache/routes.json';

const routesMap = routesCache as unknown as RoutesMap;
const routes = Object.values(routesMap) as Route[];

type PropsGql = {
  author?: Author | null;
  medicalReviewer: Author | null;
  locale: string;
};

export function getNormalizedAuthorGql({
  author,
  medicalReviewer,
  locale,
}: PropsGql): PageAuthors | null {
  if (!author && !medicalReviewer) {
    return null;
  }

  const [authorRoute] = author
    ? routes.filter((e: Route) => e.page === author.slug && e.locale === locale)
    : [];

  const [medicalAuthorRoute] = medicalReviewer
    ? routes.filter(
        (e: Route) => e.page === medicalReviewer.slug && e.locale === locale,
      )
    : [];

  let parentRoute = '';
  if (authorRoute && authorRoute.firstLevelPageSlug) {
    parentRoute = authorRoute.firstLevelPageSlug[locale];
  } else if (medicalAuthorRoute && medicalAuthorRoute.firstLevelPageSlug) {
    parentRoute = medicalAuthorRoute.firstLevelPageSlug[locale];
  }

  if (!parentRoute) {
    let sysId = null;
    if (author) {
      sysId = author.sys.id;
    } else if (medicalReviewer) {
      sysId = medicalReviewer.sys.id;
    }

    throw new Error(`Can't compute route for author ${sysId}`);
  }

  return {
    author: author
      ? {
          name: author.name || '',
          avatarImage: author.photo && author.photo.url ? author.photo.url : '',
          slug: `/${locale}/${parentRoute}/${author.slug}`,
          description: author.description || '',
        }
      : null,
    medicalAuthor: medicalReviewer
      ? {
          name: medicalReviewer.name || '',
          avatarImage:
            medicalReviewer.photo && medicalReviewer.photo.url
              ? medicalReviewer.photo.url
              : '',
          slug: `/${locale}/${parentRoute}/${medicalReviewer.slug}`,
          description: medicalReviewer.description || '',
        }
      : null,
  };
}

export function getAuthorsBaseSlug(locale?: string): string | null {
  if (!locale) {
    return null;
  }

  const pageSlugs = routes.filter(
    ({ contentType }) => contentType === 'author',
  )[0]?.firstLevelPageSlug;

  return (pageSlugs && pageSlugs[locale]) || null;
}
